"use client";

import { useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { API_BASE } from "@/lib/api";
import { useLocalizedRouter } from "@/lib/localized-router";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          prompt: (cb?: (notification: PromptMomentNotification) => void) => void;
          cancel: () => void;
        };
      };
    };
  }
}

// PromptMomentNotification is the modern (FedCM-aware) shape returned
// by google.accounts.id.prompt's callback. The legacy `isDisplayed()`
// method was removed - callers now interrogate the moment with the
// methods below to learn why the prompt didn't appear.
// https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
interface PromptMomentNotification {
  isNotDisplayed: () => boolean;
  getNotDisplayedReason: () => string;
  isSkippedMoment: () => boolean;
  getSkippedReason: () => string;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => string;
  getMomentType: () => string;
}

/**
 * Google One Tap - shows the "Continue as [Name]" overlay when the user
 * is not signed in. Loads the Google Identity Services script and
 * initializes One Tap with the app's client ID.
 *
 * On success, sends the credential (Google ID token) to the backend's
 * /api/auth/google/one-tap endpoint, which verifies it and creates/logs
 * in the user.
 */
export default function GoogleOneTap() {
  const { user, loading, setUser, refreshTokens } = useAuth();
  // The prompt costs a 96 KiB script plus console noise before anyone
  // has touched the page, so the SDK waits for the first interaction
  // (pointer, key or scroll) and then the idle callback. Residence
  // pages did this first; every page does since 2026-10-07, because
  // the SDK was still on the load path elsewhere on Google's phone run.
  const router = useLocalizedRouter();

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        const res = await fetch(`${API_BASE}/auth/google/one-tap`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            credential: response.credential,
            locale: (window.location.pathname.match(/^\/(az|ru|en)(?=\/|$)/) || [])[1] || "az",
            return_to: window.location.pathname + window.location.search,
          }),
        });

        if (!res.ok) return;

        const data = await res.json();
        setUser(data.user);
        await refreshTokens();

        if (data.is_new_user) {
          // Stay on the page the person was reading (a residence, a
          // listing, a landing: where One Tap mostly fires) and show
          // the welcome card there; WelcomeHost in the layout reads
          // the flag on any route. Before 2026-10-14 this went to the
          // home page, whose signed-in branch never mounted the card.
          const here = window.location.pathname.replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";
          const q = new URLSearchParams(window.location.search);
          q.set("welcome", "1");
          router.push(`${here}?${q.toString()}`);
        }
      } catch {
        // Silently fail - One Tap is a convenience, not critical
      }
    },
    [setUser, refreshTokens, router],
  );

  useEffect(() => {
    // Don't show if already signed in or still loading
    if (loading || user) return;

    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    // Defer the 96 KiB GSI SDK fetch until the browser is idle. Loading
    // it eagerly put it on the critical path for every product page
    // even though One Tap is a passive convenience — Lighthouse flagged
    // it as a ~300 ms TBT contributor. Gating on requestIdleCallback
    // (with a setTimeout fallback for Safari) lets LCP settle first
    // and starts the fetch only after the page is interactive. The
    // 2500 ms timeout fires even under sustained main-thread load so
    // One Tap always gets a chance to prompt.
    let script: HTMLScriptElement | null = null;
    const load = () => {
      script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => {
        window.google?.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: true, // auto-login if user previously consented
          cancel_on_tap_outside: true,
          // FedCM is required for One Tap in modern Chrome / Edge.
          // Without it, /gsi/status returns 403 because the browser
          // refuses to expose the user's Google session under the
          // tightened third-party cookie rules that landed with
          // Chrome 124+. With it, Google replaces the One-Tap card
          // with the browser's native FedCM account chooser - same
          // UX intent, supported in all evergreen browsers.
          use_fedcm_for_prompt: true,
        });
        window.google?.accounts.id.prompt((moment) => {
          // Diagnostic when the prompt doesn't show: log the reason
          // (origin not allow-listed, cool-off after dismissal,
          // suppressed by FedCM, etc.) so a "One Tap is broken"
          // report has something to grep for in the console.
          if (process.env.NODE_ENV !== "production") {
            if (moment.isNotDisplayed()) {
              // eslint-disable-next-line no-console
              console.info(
                "[GoogleOneTap] not displayed:", moment.getNotDisplayedReason(),
              );
            } else if (moment.isSkippedMoment()) {
              // eslint-disable-next-line no-console
              console.info(
                "[GoogleOneTap] skipped:", moment.getSkippedReason(),
              );
            } else if (moment.isDismissedMoment()) {
              // eslint-disable-next-line no-console
              console.info(
                "[GoogleOneTap] dismissed:", moment.getDismissedReason(),
              );
            }
          }
        });
      };
      document.head.appendChild(script);
    };

    type RIC = (cb: () => void, opts?: { timeout?: number }) => number;
    type CRIC = (handle: number) => void;
    const ric = (window as unknown as { requestIdleCallback?: RIC }).requestIdleCallback;
    const cric = (window as unknown as { cancelIdleCallback?: CRIC }).cancelIdleCallback;
    let idleId = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (ric) {
        idleId = ric(load, { timeout: 2500 });
      } else {
        timeoutId = setTimeout(load, 2500);
      }
    };
    const intentEvents = ["pointerdown", "keydown", "scroll"] as const;
    const onIntent = () => {
      intentEvents.forEach((ev) => window.removeEventListener(ev, onIntent));
      schedule();
    };
    intentEvents.forEach((ev) =>
      window.addEventListener(ev, onIntent, { passive: true, once: true }),
    );

    return () => {
      intentEvents.forEach((ev) => window.removeEventListener(ev, onIntent));
      if (idleId && cric) cric(idleId);
      if (timeoutId) clearTimeout(timeoutId);
      window.google?.accounts.id.cancel();
      script?.remove();
    };
  }, [loading, user, handleCredentialResponse]);

  // This component renders nothing - it just triggers the One Tap prompt
  return null;
}
